import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ArrowRight, CheckCircle2, XCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { isBusinessDay } from '../utils';
import { useLanguage } from '../contexts/LanguageContext';
import { MonthCalendar } from './practice/MonthCalendar';
import { PracticeProtocol } from './practice/PracticeProtocol';
import { PracticeEvaluation } from './practice/PracticeEvaluation';
import { PracticeNoteModal, PracticeHoverNote } from './practice/PracticeNoteModal';

export function PracticePage({ data, onChange }) {
  const { t, language } = useLanguage();
  const [year, setYear] = useState(() => {
    const saved = parseInt(localStorage.getItem('metodo11_practice_year'), 10);
    return Number.isFinite(saved) ? saved : new Date().getFullYear();
  });

  // Open where the user left off: jump to the year of their latest practice entry (once data loads)
  const jumpedRef = React.useRef(false);
  useEffect(() => {
    if (jumpedRef.current) return;
    const keys = Object.keys(data || {}).filter(k => /^\d{4}-/.test(k)).sort();
    if (keys.length > 0) {
      jumpedRef.current = true;
      const y = parseInt(keys[keys.length - 1].slice(0, 4), 10);
      if (Number.isFinite(y)) setYear(y);
    }
  }, [data]);

  useEffect(() => {
    localStorage.setItem('metodo11_practice_year', String(year));
  }, [year]);
  const [confirmReset, setConfirmReset] = useState(null);
  const [noteModal, setNoteModal] = useState(null); // { dateKey, currentNote }
  const [hoverNote, setHoverNote] = useState(null); // { dateKey, note }

  const practiceData = data || {};
  const setPracticeData = (updater) => {
    const newData = typeof updater === 'function' ? updater(practiceData) : updater;
    onChange(newData);
  };

  const handleReset = () => {
    if (!confirmReset) return;

    if (confirmReset.type === 'month' && confirmReset.index !== undefined) {
      const monthIndex = confirmReset.index;
      setPracticeData((prev) => {
        const newData = { ...prev };
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
          const key = `${year}-${monthIndex + 1}-${d}`;
          delete newData[key];
        }
        return newData;
      });
    } else if (confirmReset.type === 'year') {
      setPracticeData((prev) => {
        const newData = { ...prev };
        for (let m = 0; m < 12; m++) {
          const daysInMonth = new Date(year, m + 1, 0).getDate();
          for (let d = 1; d <= daysInMonth; d++) {
            const key = `${year}-${m + 1}-${d}`;
            delete newData[key];
          }
        }
        return newData;
      });
    }
    setConfirmReset(null);
  };

  const months = [
    t.practice.january, t.practice.february, t.practice.march, t.practice.april, t.practice.may, t.practice.june,
    t.practice.july, t.practice.august, t.practice.september, t.practice.october, t.practice.november, t.practice.december
  ];

  const toggleDay = (dateKey, isAllowed) => {
    if (!isAllowed) return;
    setPracticeData((prev) => {
      // Support both old format (number) and new format (object)
      const current = prev[dateKey];
      const currentStatus = typeof current === 'object' ? current.status : current;
      const currentNote = typeof current === 'object' ? current.note : '';

      // Toggle status: 1 → 0 → undefined → 1
      const nextStatus = currentStatus === 1 ? 0 : currentStatus === 0 ? undefined : 1;
      const newData = { ...prev };

      if (nextStatus === undefined && !currentNote) {
        delete newData[dateKey];
      } else if (nextStatus === undefined && currentNote) {
        newData[dateKey] = { note: currentNote };
      } else if (currentNote) {
        newData[dateKey] = { status: nextStatus, note: currentNote };
      } else {
        newData[dateKey] = nextStatus;
      }

      return newData;
    });
  };

  const saveNote = (dateKey, note) => {
    setPracticeData((prev) => {
      const current = prev[dateKey];
      const currentStatus = typeof current === 'object' ? current.status : current;
      const newData = { ...prev };

      if (note.trim()) {
        if (currentStatus !== undefined) {
          newData[dateKey] = { status: currentStatus, note: note.trim() };
        } else {
          newData[dateKey] = { note: note.trim() };
        }
      } else if (currentStatus !== undefined) {
        newData[dateKey] = currentStatus;
      } else {
        delete newData[dateKey];
      }

      return newData;
    });
    setNoteModal(null);
  };

  const openNoteModal = (dateKey, e) => {
    e.stopPropagation();
    const current = practiceData[dateKey];
    const currentNote = typeof current === 'object' ? current.note || '' : '';
    setNoteModal({ dateKey, currentNote });
  };

  const getDayData = (dateKey) => {
    const data = practiceData[dateKey];
    if (typeof data === 'object') {
      return { status: data.status, note: data.note || '' };
    }
    return { status: data, note: '' };
  };

  const getMonthStats = (monthIndex) => {
    let total = 0;
    let positive = 0;
    let negative = 0;
    let selectedCount = 0;

    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, monthIndex, d);
      if (isBusinessDay(date)) {
        total++;
        const key = `${year}-${monthIndex + 1}-${d}`;
        const val = getDayData(key).status;

        if (val !== undefined) {
          selectedCount++;
          if (val === 1) positive++;
          else negative++;
        }
      }
    }

    const winRate = total > 0 ? (positive / total) * 100 : 0;
    return { total, positive, negative, winRate, selectedCount };
  };

  const getYearlyStats = () => {
    let totalDays = 0;
    let positiveDays = 0;
    let negativeDays = 0;
    let maxConsecutiveErrors = 0;
    let currentConsecutiveErrors = 0;

    for (let m = 0; m < 12; m++) {
      const daysInMonth = new Date(year, m + 1, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, m, d);

        if (isBusinessDay(date)) {
          totalDays++;
          const key = `${year}-${m + 1}-${d}`;
          const val = getDayData(key).status;

          if (val !== undefined) {
            if (val === 1) {
              positiveDays++;
              currentConsecutiveErrors = 0;
            } else if (val === 0) {
              negativeDays++;
              currentConsecutiveErrors++;
              maxConsecutiveErrors = Math.max(maxConsecutiveErrors, currentConsecutiveErrors);
            }
          } else {
            currentConsecutiveErrors = 0;
          }
        }
      }
    }

    const yearlyWinRate = totalDays > 0 ? (positiveDays / totalDays) * 100 : 0;
    const yearlyNegativeRate = totalDays > 0 ? (negativeDays / totalDays) * 100 : 0;

    let lastMonthWithData = -1;
    let lastMonthWinRate = 0;
    let lastMonthSelectedDays = 0;
    let lastMonthTotalDays = 0;
    for (let m = 11; m >= 0; m--) {
      const stats = getMonthStats(m);
      if (stats.selectedCount > 0) {
        lastMonthWithData = m;
        lastMonthWinRate = stats.winRate;
        lastMonthSelectedDays = stats.selectedCount;
        lastMonthTotalDays = stats.total;
        break;
      }
    }

    return {
      yearlyWinRate,
      yearlyNegativeRate,
      maxConsecutiveErrors,
      lastMonthWinRate,
      lastMonthSelectedDays,
      lastMonthTotalDays,
      lastMonthName: lastMonthWithData !== -1 ? months[lastMonthWithData] : 'N/A',
      lastMonthWithData,
      totalDays,
      positiveDays,
      negativeDays
    };
  };

  const yearlyStats = getYearlyStats();

  const monthCompletionRate = yearlyStats.lastMonthTotalDays > 0 ? (yearlyStats.lastMonthSelectedDays / yearlyStats.lastMonthTotalDays) * 100 : 0;
  const isMonthComplete = monthCompletionRate >= 90;

  let status = {
    title: t.practice.continueSim,
    color: "red",
    icon: <XCircle className="text-red-500" size={32} />,
    description: !isMonthComplete
      ? `${t.practice.missingData}: ${monthCompletionRate.toFixed(0)}% ${t.practice.missingDataDesc}`
      : t.practice.disciplineLow,
    details: [
      `${t.practice.monthlyCompletion}: ${monthCompletionRate.toFixed(0)}% / 90%`,
      t.practice.disciplineLowDetail,
      t.practice.consecutiveErrorsDetail
    ]
  };

  // Solo diciembre (mes 11) puede dar "Aprobado para Real"
  const isDecember = yearlyStats.lastMonthWithData === 11;

  if (isMonthComplete && yearlyStats.maxConsecutiveErrors < 2) {
    if (yearlyStats.lastMonthWinRate >= 90 && isDecember) {
      status = {
        title: t.practice.approvedReal,
        color: "emerald",
        icon: <CheckCircle2 className="text-emerald-500" size={32} />,
        description: t.practice.approvedDesc,
        details: [
          t.practice.monthlyCompletionDetail,
          t.practice.disciplineHighDetail,
          t.practice.maxOneErrorDetail
        ]
      };
    } else if (yearlyStats.lastMonthWinRate >= 85) {
      status = {
        title: t.practice.observation,
        color: "amber",
        icon: <AlertTriangle className="text-amber-500" size={32} />,
        description: t.practice.observationDesc,
        details: [
          t.practice.monthlyCompletionDetail,
          t.practice.disciplineMidDetail,
          t.practice.maxOneErrorDetail,
          t.practice.detailedReviewDetail
        ]
      };
    }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8 xl:items-start" data-testid="practice-page">
      {/* Calendar Section */}
      <div className="space-y-6 sm:space-y-8 xl:h-full">
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100 relative">
          {confirmReset && (
            <div className="absolute inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 rounded-2xl">
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl border border-gray-200 max-w-sm w-full text-center space-y-4">
                <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                  <RotateCcw size={24} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-gray-900">
                    {confirmReset.type === 'month' ? t.practice.resetMonth : t.practice.resetYear}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {confirmReset.type === 'month' ? t.practice.resetMonthConfirm : t.practice.resetYearConfirm}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmReset(null)}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg transition-colors text-sm"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors text-sm"
                  >
                    {t.common.confirm}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="text-indigo-600" /> {t.practice.disciplineCalendar}
            </h2>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={() => setConfirmReset({ type: 'year' })}
                title={t.practice.resetYear}
                data-testid="practice-year-reset"
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all border border-transparent hover:border-red-100"
              >
                <RotateCcw size={18} />
              </button>
              <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-lg border border-gray-200">
                <button
                  onClick={() => setYear(y => y - 1)}
                  className="p-1 hover:bg-white hover:shadow-sm rounded transition-all"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                </button>
                <span className="font-bold text-gray-700 min-w-[50px] sm:min-w-[60px] text-center text-sm">{year}</span>
                <button
                  onClick={() => setYear(y => y + 1)}
                  className="p-1 hover:bg-white hover:shadow-sm rounded transition-all"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2 gap-4 sm:gap-6">
            {months.map((monthName, mIdx) => (
              <MonthCalendar
                key={monthName}
                monthName={monthName}
                mIdx={mIdx}
                year={year}
                stats={getMonthStats(mIdx)}
                t={t}
                language={language}
                getDayData={getDayData}
                toggleDay={toggleDay}
                openNoteModal={openNoteModal}
                setHoverNote={setHoverNote}
                onResetMonth={() => setConfirmReset({ type: 'month', index: mIdx })}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Protocol and Evaluation Section */}
      <div className="space-y-4 sm:space-y-6 xl:h-full">
        <PracticeProtocol t={t} />
        <PracticeEvaluation
          year={year}
          status={status}
          yearlyStats={yearlyStats}
          monthCompletionRate={monthCompletionRate}
          isMonthComplete={isMonthComplete}
          language={language}
          t={t}
        />
      </div>

      <PracticeNoteModal noteModal={noteModal} language={language} onClose={() => setNoteModal(null)} onSave={saveNote} />
      <PracticeHoverNote hoverNote={hoverNote} />
    </div>
  );
}
